import { ToastrService } from 'ngx-toastr';
import {Action, createFeatureSelector, createSelector, select} from '@ngrx/store';
import {Injectable} from '@angular/core';
import {Actions, Effect, ofType} from '@ngrx/effects';
import {catchError, exhaustMap, filter, map, take} from 'rxjs/operators';
import {forkJoin, of} from 'rxjs';
// Payload Types
import {BaseStateV2} from '../../core/base.state';
import {Service} from './service';
import {getProfile as userProfile} from '../../core/auth/store';
import * as FolderStore from '../folder/store';
import {CoreStore} from '../../core/core.store';
import * as UILoaderStore from '../../shared/loader/store';
import * as _ from 'lodash';
export const FEATURE_NAME       = 'SHARE';
export const RESOURCE: string   = 'shares';
export const FeatureActions     = new CoreStore.Actions(FEATURE_NAME);


export interface IState extends CoreStore.State {
    file: any;
    expiration: any;
    activate: boolean;
    formBase: any;
}

export const initialState: IState = {
    ...CoreStore.initialState,
    file: undefined,
    expiration: undefined,
    activate: false,
    formBase: undefined
};

export function reducer(state = initialState, action: any): any {
    return CoreStore.reducer(state, action, FeatureActions)
}

const selectState = createFeatureSelector<IState>(FEATURE_NAME);

export const selectors = {
    ...CoreStore.selectors(FEATURE_NAME),
    file: createSelector(selectState, (state: IState) => state.file),
    expiration: createSelector(selectState, (state: IState) => state.expiration),
    activate: createSelector(selectState, (state: IState) => state.activate),
    formBase: createSelector(selectState, (state: IState) => state.formBase)
};

// Effects

@Injectable()
export class Effects {

    private _coreEffectsOptions: CoreStore.EffectsOptions = {
        FEATURE_NAME: FEATURE_NAME,
        Actions: FeatureActions,
        featureService: this._svc,
        actions$: this.actions$
    };

    @Effect()
    Load$ = CoreStore.Effects.Load(this._coreEffectsOptions,
        (obj) => 'shares?sharee=' + obj.sharee);

    @Effect()
    CreateMultiple$ = this._coreEffectsOptions.actions$.pipe(
        ofType(this._coreEffectsOptions.Actions.Types.Create),
        map((action: any) => action.payload),
        exhaustMap((obj) =>
            forkJoin(..._.map(_.flatten([obj]), obj =>
                this._svc.doAdd('files/' + obj.id + '/shares?sharee=' + obj.sharee + '&expiresIn=' + obj.expiresIn))
            )
                .pipe(
                    map(data => new this._coreEffectsOptions.Actions.CreateSuccess(data)),
                    catchError(error => of(new this._coreEffectsOptions.Actions.CreateFailure(error)))
                ),
        )
    );

    @Effect()
    DeleteMultiple$ = this._coreEffectsOptions.actions$.pipe(
        ofType(this._coreEffectsOptions.Actions.Types.Delete),
        map((action: any) => action.payload),
        exhaustMap((obj) =>
            forkJoin(
                ..._.map(_.flatten([obj]), obj =>
                    this._svc.doDelete('shares/' + obj.id)
                )
            )
                .pipe(
                    map(data => new this._coreEffectsOptions.Actions.DeleteSuccess(data)),
                    catchError(error => of(new this._coreEffectsOptions.Actions.DeleteFailure(error)))
                ),
        )
    );


    @Effect()
    ActionSuccess$ = CoreStore.Effects.ActionSuccess(this._coreEffectsOptions);

    @Effect()
    ActionFailure$ = CoreStore.Effects.ActionFailure(this._coreEffectsOptions);

    @Effect()
    SuccessReset$ = CoreStore.Effects.SuccessReset(this._coreEffectsOptions);


    @Effect()
    FlushOnLogout$ = CoreStore.Effects.FlushOnLogout(this._coreEffectsOptions);


    @Effect()
    ReloadFolders$ = this.actions$.pipe(
        ofType(FeatureActions.Types.CreateSuccess,
            FeatureActions.Types.DeleteSuccess,
            FeatureActions.Types.CreateShareSuccess),
        map((action: any) => action.payload),
        exhaustMap((file) => of(new FolderStore.FeatureActions.Load({ id: file[0].parent.id })))
    );

    @Effect()
    MergeFoldersOnLoadSuccess$ = this.actions$.pipe(
        ofType(FeatureActions.Types.LoadSuccess),
        map((action: any) => action.payload),
        exhaustMap((shares: any) =>
            of({type: FolderStore.FeatureActions.Types.MergeChildren, payload: shares})
        )
    );

    @Effect()
    CreateShare$ = this._coreEffectsOptions.actions$.pipe(
        ofType(this._coreEffectsOptions.Actions.Types.CreateShare),
        map((action: any) => action.payload),
        exhaustMap((obj) =>
        this._coreEffectsOptions.featureService.createShare({
            itemId: obj.itemId,
            shareeList: obj.shareeList,
            expiresIn: obj.expiresIn
        }, "createShare")
            .pipe(
                map(data => {
                    this.toastrService.success('', 'The sharee list has been updated successfully.', {
                        timeOut: 3000
                    });
                    if(obj.isMyFilesSection) {
                        return (new FolderStore.FeatureActions.Load({
                            id: data.parent.id,
                            loadMyFiles: true
                        }));
                    }
                    else {
                        return (new this._coreEffectsOptions.Actions.CreateShareSuccess([data]));
                    }

                }),
                catchError(error => {
                    this.toastrService.error(
                        '<b>If the problem persists, please contact an administrator.</b>',
                        'An error occured while sharing the item. Please try again.',
                        {
                            closeButton: true,
                            disableTimeOut: true,
                            enableHtml: true
                        }
                    );
                    return error;
                })
            )
        )
    );

    //TODO: need better way of manipulating another Feature's store
    @Effect()
    StartUILoader$ = this.actions$.pipe(
        ofType(FeatureActions.Types.Create,
            FeatureActions.Types.Delete,
            FeatureActions.Types.Update),
        exhaustMap(() => of({
            type: UILoaderStore.FeatureActions.Types.Set,
            payload: { isLoading: true }
        }))
    );

    @Effect()
    EndUILoader$ = this.actions$.pipe(
        ofType(
            FeatureActions.Types.Success,
            FeatureActions.Types.Failure
        ),
        exhaustMap(() => of({
            type: UILoaderStore.FeatureActions.Types.Set,
            payload: { isLoading: false }
        }))
    );


    constructor( protected actions$: Actions<any>,  protected _svc: Service, public toastrService: ToastrService) {}
}

@Injectable()
export class State extends BaseStateV2 {

    FEATURE_NAME: string = FEATURE_NAME;
    FeatureActions: CoreStore.Actions = FeatureActions;
    _selectors = selectors;

    async load() {
        this._store.pipe(
            select(userProfile),
            take(1),
            map(profile => this._store.dispatch(new FeatureActions.Load({ sharee: profile.id })) )
        ).toPromise().then();
        return this.success
    }

    set file(file) {
        this.set({file: file})
    }

    get file(){
        return this._store
            .pipe(select(this._selectors.file))
            .pipe(filter(exists => exists))
    }

    create(obj): any {
        return super.create(obj)
    }

    remove(obj): any {
        return super.remove(obj)
    }

    async createShare(obj) {
        this._store.dispatch(new this.FeatureActions.CreateShare(obj));
    }

    set active(isActive: any) {
        this.set({activate: isActive})
    }

    get isActive(){
        return this._store
            .pipe(select(selectors.activate))
            .pipe(filter(exists => exists))
    }

    get formBase(){
        return this._store
            .pipe(select(selectors.formBase))
            .pipe(filter(exists => exists))
    }

    set formBase(formBase){
        this.set({ formBase: formBase })
    }

    activate(users, formBase, fileItem){


        this.formBase = formBase;
        this.file     = fileItem;

        setTimeout(() => {
            this.active = true
        });

    }

    deactivate(){
        this.active = false;
        this.file = undefined;
    }
}
